import * as React from 'react';
import { useEffect, useRef } from 'react';

import L, { version as LEAFLET_VERSION } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapContainer, Marker, TileLayer, useMap } from 'react-leaflet';

// ─── Leaflet Default Icon Fix ─────────────────────────────────────────────────
// This package builds with tsup/esbuild (no Vite-style static asset pipeline — `leaflet` is
// marked `external` in tsup.config.ts and no asset-copy step exists), so the default marker
// icons can't be bundled as local files here — point at the unpkg CDN copies instead, versioned
// from leaflet's own exported `version` so the URL can never drift from the installed package.
// Implication: consumers load these PNGs from a third-party CDN at runtime — any CSP `img-src`
// policy must allow `unpkg.com`, and availability depends on unpkg's uptime.

delete (L.Icon.Default.prototype as unknown as Record<string, unknown>)['_getIconUrl'];
L.Icon.Default.mergeOptions({
  iconRetinaUrl: `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/images/marker-icon-2x.png`,
  iconUrl: `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/images/marker-icon.png`,
  shadowUrl: `https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/images/marker-shadow.png`,
});

// ─── Constants ────────────────────────────────────────────────────────────────

const DEFAULT_ZOOM = 16;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AddressMapProps {
  /** Tile layer attribution (e.g. HERE, OSM/CARTO) — the app owns the tile provider choice. */
  attribution: string;
  lat: number;
  lng: number;
  onPositionChange: (lat: number, lng: number) => void;
  /** Tile layer URL template — the app owns the tile provider choice (e.g. HERE Maps logistics.day). */
  tileUrl: string;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface MapCenterUpdaterProps {
  lat: number;
  lng: number;
}

function MapCenterUpdater({ lat, lng }: MapCenterUpdaterProps): null {
  const map = useMap();
  const prevRef = useRef<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    const prev = prevRef.current;
    if (!prev || prev.lat !== lat || prev.lng !== lng) {
      map.setView([lat, lng], map.getZoom());
      prevRef.current = { lat, lng };
    }
  }, [lat, lng, map]);

  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Draggable-pin address map. Tile provider is injected via `tileUrl`/`attribution` — this package has no app config access. */
export function AddressMap({ attribution, lat, lng, onPositionChange, tileUrl }: AddressMapProps): React.JSX.Element {
  const markerRef = useRef<L.Marker | null>(null);

  const handleDragEnd = (): void => {
    const marker = markerRef.current;
    if (marker) {
      const position = marker.getLatLng();
      onPositionChange(position.lat, position.lng);
    }
  };

  return (
    <div className="relative z-0 h-48 w-full overflow-hidden rounded-lg border">
      <MapContainer center={[lat, lng]} className="h-full w-full" scrollWheelZoom={false} zoom={DEFAULT_ZOOM}>
        <TileLayer attribution={attribution} url={tileUrl} />
        <MapCenterUpdater lat={lat} lng={lng} />
        <Marker draggable eventHandlers={{ dragend: handleDragEnd }} position={[lat, lng]} ref={markerRef} />
      </MapContainer>
    </div>
  );
}
